import React, { useState, useRef, useCallback, useMemo } from 'react';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { useTheme } from '@mui/material/styles';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { duplicateSegment, removeSegment as removeSegmentAction, toggleSegmentLock } from '@/redux/slices/packagingTimelineSlice';
import { setActiveCutType, setActiveHover, clearActiveHover } from '@/redux/slices/packagingSlice';
import { DRAGGABLE_TYPES, type Milliseconds, type SegmentDraggableData, type SegmentItem } from '@/redux/slices/packagingSlice.types';
import { selectIsReadOnlyPackaging, selectPackagingTimelineFrameDuration } from '@/redux/selectors/packagingSelectors';
import { getOptionColor, isBodySegmentKind } from '@/components/Packaging/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import Toolbar from '@/components/Packaging/LayeredTimeline/common/Toolbar';
import SegmentDuration from '@/components/Packaging/LayeredTimeline/segments/SegmentDuration';
import GripIcon from '@/components/Packaging/LayeredTimeline/common/GripIcon';
import {
  MILLISECONDS_PER_PIXEL,
  SEGMENT_HEIGHT,
  SEGMENTS_GAP,
  UNASSIGNED_SEGMENT_BORDER_COLOR_DARK_THEME,
  UNASSIGNED_SEGMENT_BORDER_COLOR_LIGHT_THEME,
  UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_DARK_THEME,
  UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_LIGHT_THEME,
  UNASSIGNED_SEGMENT_GRIP_COLOR_DARK_THEME,
  UNASSIGNED_SEGMENT_GRIP_COLOR_LIGHT_THEME
} from '@/components/Packaging/constants';
import UnlockButton from '@/components/Packaging/LayeredTimeline/common/UnlockButton';
import { selectCutTypeLabels } from '@/redux/selectors/constantsSelectors';
import { ConditionalTooltip } from '@/exports/columnCommonComponents';

interface SegmentProps {
  segment: SegmentItem;
  scale: number;
  isEditingTemplate?: boolean;
}

const getSegmentWidth = (duration: Milliseconds, scale: number): number => {
  return (duration * scale) / MILLISECONDS_PER_PIXEL;
};

const Segment: React.FC<SegmentProps> = ({ segment, scale, isEditingTemplate }) => {
  const dispatch = useAppDispatch();
  const frameDuration = useAppSelector(selectPackagingTimelineFrameDuration);
  const theme = useTheme();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);
  const color = getOptionColor(segment.kind);
  const cutTypeLabels = useAppSelector(selectCutTypeLabels);
  const isReadOnly = useAppSelector(selectIsReadOnlyPackaging);

  const hasAssignedInventory = Boolean(segment.source);
  const { isLocked, isSuperLocked } = segment;
  const isDarkTheme = theme.palette.mode === 'dark';
  const gripIconColor = useMemo(() => {
    if (hasAssignedInventory && !isLocked) {
      return color;
    }
    return isDarkTheme ? UNASSIGNED_SEGMENT_GRIP_COLOR_DARK_THEME : UNASSIGNED_SEGMENT_GRIP_COLOR_LIGHT_THEME;
  }, [hasAssignedInventory, isLocked, color, isDarkTheme]);
  const subtitleColor = !isDarkTheme && isLocked && hasAssignedInventory ? '#fff' : undefined;
  const unlockButtonColor = hasAssignedInventory ? '#9bd4d1' : isDarkTheme ? '#ffffff' : '#000000';

  const data: SegmentDraggableData = {
    type: DRAGGABLE_TYPES.SEGMENT,
    kind: segment.kind
  };
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: segment.draggableId,
    data,
    disabled: isReadOnly
  });

  const segmentFramesCount: number = segment.end - segment.start;
  const segmentDuration: Milliseconds = segmentFramesCount * frameDuration;
  const width = getSegmentWidth(segmentDuration, scale);
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: isReadOnly ? 'default' : isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 1 : 'auto',
    width: `${width}px`,
    paddingRight: Math.min(width, SEGMENTS_GAP)
  };

  const isToolbarInPopper = width < 140;
  const isDurationHidden = isLocked ? width < 88 : width < 90;

  const toggleLock = () => {
    dispatch(toggleSegmentLock(segment.draggableId));
  };

  const refCombined = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    wrapperRef.current = node;
  };

  const removeSegment = () => dispatch(removeSegmentAction(segment.draggableId));

  const handleDuplicateButtonClick = useCallback(() => {
    dispatch(duplicateSegment(segment.draggableId));
  }, [segment.draggableId, isLocked, isSuperLocked]);

  const handleAssignInventoryButtonClick = () => {
    if (isLocked || isSuperLocked) return;
    dispatch(setActiveCutType(segment));
  };

  const handleRemoveButtonClick = () => {
    if (isLocked || isSuperLocked) return;
    removeSegment();
  };

  const handleMouseEnter = () => {
    setIsToolbarOpen(true);
    dispatch(setActiveHover([segment]));
  };

  const handleMouseLeave = () => {
    setIsToolbarOpen(false);
    dispatch(clearActiveHover());
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    if (['Delete', 'Backspace'].includes(event.key) && !isLocked && !isSuperLocked) {
      removeSegment();
    } else if (listeners?.onKeyDown) {
      listeners.onKeyDown(event);
    }
  };

  const isAssignInventoryButtonVisible =
    !isSuperLocked &&
    // we don't want to allow users assigning Body segments in the template editor, cause template's body segments should not store `source`
    !(isEditingTemplate && isBodySegmentKind(segment.kind));

  const content = (
    <ConditionalTooltip title="This segment is protected from changes." show={(isSuperLocked || false) && !isReadOnly} placement="top" arrow>
      <Container locked={isLocked} color={color} intermediate={segment.isIntermediate} hasAssignedInventory={hasAssignedInventory}>
        {width > 32 && !isReadOnly && <GripIcon color={gripIconColor} />}
        {!isLocked && !isReadOnly && (
          <ToolbarWrapper isInPopper={isToolbarInPopper}>
            <Toolbar
              isInPopper={isToolbarInPopper}
              isPopperOpen={isToolbarOpen && !isDragging}
              segmentWrapperElement={wrapperRef.current}
              hasAssignedInventory={hasAssignedInventory}
              onDuplicateButtonClick={!isSuperLocked ? handleDuplicateButtonClick : undefined}
              onAssignInventoryButtonClick={isAssignInventoryButtonVisible ? handleAssignInventoryButtonClick : undefined}
              isLocked={isLocked}
              onLockButtonClick={!isSuperLocked ? toggleLock : () => {}}
              color={color}
              onRemoveButtonClick={handleRemoveButtonClick}
            />
          </ToolbarWrapper>
        )}

        <Clip locked={isLocked}>
          <Label locked={isLocked}>
            <Title locked={isLocked} hasAssignedInventory={hasAssignedInventory}>
              {width > 36 && <TitleIcon icon="cards" height={20} width={20} locked={isLocked} />}
              {width > 68 && <span>{cutTypeLabels[segment.kind] ?? segment.label}</span>}
            </Title>
            {width < 56 ? null : (
              <Subtitle locked={isLocked} color={subtitleColor}>
                {segment.subtitle}
              </Subtitle>
            )}
          </Label>
          {isLocked && !isSuperLocked && <UnlockButton color={unlockButtonColor} onClick={toggleLock} />}
          {hasAssignedInventory && !isDurationHidden && <SegmentDuration framesCount={segmentFramesCount} color={color} isLocked={isLocked} />}
        </Clip>
      </Container>
    </ConditionalTooltip>
  );

  return (
    <Wrapper
      ref={refCombined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={style}
      color={color}
      hasAssignedInventory={hasAssignedInventory}
      {...attributes}
      data-prevent-timeline-seek
      data-segment-id={segment.draggableId}
      /* order of props below matters */
      {...listeners}
      onKeyDown={handleKeyDown}
    >
      {content}
    </Wrapper>
  );
};

export default React.memo(Segment);

const Wrapper = styled.div<{ color: string; hasAssignedInventory: boolean }>`
  &:focus > div {
    box-shadow: inset 0 0 7px
      ${({ theme, color, hasAssignedInventory }) => {
        const isDarkTheme = theme.palette.mode === 'dark';

        if (!hasAssignedInventory) {
          return isDarkTheme ? UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_DARK_THEME : UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_LIGHT_THEME;
        }

        return `${color}${isDarkTheme ? 'ff' : '66'}`;
      }};
  }
`;

const Container = styled.div<{ color: string; locked: boolean; intermediate: boolean; hasAssignedInventory: boolean }>`
  position: relative;
  height: ${SEGMENT_HEIGHT}px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border-width: 1px;
  border-style: ${({ hasAssignedInventory }) => (!hasAssignedInventory ? 'dashed' : 'solid')};
  border-color: ${({ theme, locked, color, hasAssignedInventory }) => {
    if (theme.palette.mode === 'dark') {
      if (!hasAssignedInventory) return UNASSIGNED_SEGMENT_BORDER_COLOR_DARK_THEME;
      return locked ? '#BDC3F2' : color;
    } else {
      if (!hasAssignedInventory) {
        return UNASSIGNED_SEGMENT_BORDER_COLOR_LIGHT_THEME;
      }

      return locked ? 'transparent' : color;
    }
  }};
  background: ${({ theme, color, locked, hasAssignedInventory }) => {
    const lockedBackgroundColor = '#4553C4B2';

    if (theme.palette.mode === 'dark') {
      if (!hasAssignedInventory) {
        return 'rgba(255, 255, 255, 0.1)';
      }

      return locked ? lockedBackgroundColor : `rgb(48 55 113 / 85%)`;
    } else {
      if (!hasAssignedInventory) {
        return 'rgba(0, 0, 0, 0.07)';
      }

      return locked ? lockedBackgroundColor : color + '40';
    }
  }};
  ${({ intermediate }) => (intermediate ? `opacity: 0.5;` : ``)}
`;

const Clip = styled.div<{ locked: boolean }>`
  position: relative;
  height: 100%;
  overflow: hidden;
  display: flex;
  padding-top: 10px;
  border-radius: ${({ theme }) => theme.shape.borderRadius - 1}px;
  align-items: ${({ locked }) => (locked ? 'start' : 'center')};
`;

const ToolbarWrapper = styled.div<{ isInPopper: boolean }>`
  position: absolute;
  z-index: 1;
  ${({ isInPopper, theme }) => {
    return isInPopper ? '' : `right: ${theme.spacing(1)};`;
  }}
`;

const Label = styled.div<{ locked: boolean }>`
  width: 100%;
  margin-top: ${({ theme, locked }) => (locked ? theme.spacing(1) : '')};
  padding: ${({ theme, locked }) => theme.spacing(0, locked ? 3 : 2, 1, 2)};
  white-space: nowrap;
  text-align: ${({ locked }) => (locked ? 'left' : 'center')};
`;

const Title = styled.div<{ locked: boolean; hasAssignedInventory: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: ${({ locked }) => (!locked ? 'center' : '')};
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(0.5)};
  font-weight: ${({ theme, hasAssignedInventory }) => (hasAssignedInventory ? theme.typography.fontWeightBold : 'normal')};
  font-size: 1rem;
  color: ${({ theme, locked, hasAssignedInventory }) => (theme.palette.mode === 'light' && locked && hasAssignedInventory ? '#fff' : undefined)};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TitleIcon = styled(PolarisIcon, {
  shouldForwardProp: (prop) => !['locked'].includes(prop)
})<{ locked: boolean }>`
  flex-shrink: 0;
  display: ${({ theme, locked }) => (theme.palette.mode === 'light' && locked ? 'none' : '')};
`;

const Subtitle = styled.div<{ locked: boolean; color: string | undefined }>`
  width: 100%;
  font-size: 0.9rem;
  font-weight: 400;
  text-align: ${({ locked }) => (locked ? 'left' : 'center')};
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  opacity: 0.66;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ color }) => color};
`;
